import type { GameState, Piece, PyramidLayer, Side, SquareKey, VictoryConfig } from "./types";
import {
  BLACK_PYRAMID_LAYERS,
  BLACK_PYRAMID_VALUE,
  WHITE_PYRAMID_LAYERS,
  WHITE_PYRAMID_VALUE,
} from "./constants";
import { squareKey } from "./coords";

/**
 * Fulke V1 ("first kynde") starting layout.
 *
 * Board orientation: 16 ranks × 8 files (A..H). White (Evens) occupies ranks 2..5 at the top
 * of the board; Black (Odds) occupies ranks 10..13 at the bottom. Each side has two empty
 * ranks behind its line — these are the "back ranks" where captured pieces will re-enter
 * once Phase 5 of the V1 implementation is in.
 *
 * Layout reproduced from the Fulke board diagram in Rithmomachia-Rules-Fulke.md:
 *
 *   engine rank (0-based) | A    B    C    D    E    F    G    H
 *   ----------------------|-------------------------------------------
 *    0   (algebraic 1)    |  .    .    .    .    .    .    .    .   white back rank #1
 *    1   (algebraic 2)    |  .    .    .    .    .    .    .    .   white back rank #2
 *    2   (algebraic 3)    | □25  □81   .    .    .    .   □169 □289 white squares — outer flanks
 *    3   (algebraic 4)    | □15  □45  △25  △20  △42  △49  K91  □153 white squares + triangles + KING (G4)
 *    4   (algebraic 5)    | △9   △6   ○4   ○16  ○36  ○64  △72  △81 white triangles + rounds, middle
 *    5   (algebraic 6)    |  .    .   ○2   ○4   ○6   ○8    .    .  white front rank — center rounds
 *    6..9                 |  .    .    .    .    .    .    .    .  no-man's-land
 *   10   (algebraic 11)   |  .    .   ○9   ○7   ○5   ○3    .    .  black front rank — center rounds
 *   11   (algebraic 12)   | △100 △90  ○81  ○49  ○25  ○9   △12  △16 black triangles + rounds, middle
 *   12   (algebraic 13)   | K190 □120 △64  △56  △36  △30  □66  □28 black KING (A13) + squares + triangles
 *   13   (algebraic 14)   | □361 □225  .    .    .    .   □121 □49 black squares — outer flanks
 *   14   (algebraic 15)   |  .    .    .    .    .    .    .    .  black back rank #2
 *   15   (algebraic 16)   |  .    .    .    .    .    .    .    .  black back rank #1
 *
 * Black moves first (Fulke explicit).
 */

interface Placement {
  file: number;
  rank: number;
  shape: "round" | "triangle" | "square";
  value: number;
}

interface PyramidPlacement {
  file: number;
  rank: number;
  side: Side;
}

const WHITE_PLACEMENTS: ReadonlyArray<Placement> = [
  // rank 2 — outer-flank squares
  { file: 0, rank: 2, shape: "square", value: 25 },
  { file: 1, rank: 2, shape: "square", value: 81 },
  { file: 6, rank: 2, shape: "square", value: 169 },
  { file: 7, rank: 2, shape: "square", value: 289 },

  // rank 3 — mixed (King at G3 = file 6, rank 3, placed separately below)
  { file: 0, rank: 3, shape: "square", value: 15 },
  { file: 1, rank: 3, shape: "square", value: 45 },
  { file: 2, rank: 3, shape: "triangle", value: 25 },
  { file: 3, rank: 3, shape: "triangle", value: 20 },
  { file: 4, rank: 3, shape: "triangle", value: 42 },
  { file: 5, rank: 3, shape: "triangle", value: 49 },
  { file: 7, rank: 3, shape: "square", value: 153 },

  // rank 4 — triangles + rounds
  { file: 0, rank: 4, shape: "triangle", value: 9 },
  { file: 1, rank: 4, shape: "triangle", value: 6 },
  { file: 2, rank: 4, shape: "round", value: 4 },
  { file: 3, rank: 4, shape: "round", value: 16 },
  { file: 4, rank: 4, shape: "round", value: 36 },
  { file: 5, rank: 4, shape: "round", value: 64 },
  { file: 6, rank: 4, shape: "triangle", value: 72 },
  { file: 7, rank: 4, shape: "triangle", value: 81 },

  // rank 5 — center 4 rounds
  { file: 2, rank: 5, shape: "round", value: 2 },
  { file: 3, rank: 5, shape: "round", value: 4 },
  { file: 4, rank: 5, shape: "round", value: 6 },
  { file: 5, rank: 5, shape: "round", value: 8 },
];

const BLACK_PLACEMENTS: ReadonlyArray<Placement> = [
  // rank 10 — center 4 rounds (front rank)
  { file: 2, rank: 10, shape: "round", value: 9 },
  { file: 3, rank: 10, shape: "round", value: 7 },
  { file: 4, rank: 10, shape: "round", value: 5 },
  { file: 5, rank: 10, shape: "round", value: 3 },

  // rank 11 — triangles + rounds
  { file: 0, rank: 11, shape: "triangle", value: 100 },
  { file: 1, rank: 11, shape: "triangle", value: 90 },
  { file: 2, rank: 11, shape: "round", value: 81 },
  { file: 3, rank: 11, shape: "round", value: 49 },
  { file: 4, rank: 11, shape: "round", value: 25 },
  { file: 5, rank: 11, shape: "round", value: 9 },
  { file: 6, rank: 11, shape: "triangle", value: 12 },
  { file: 7, rank: 11, shape: "triangle", value: 16 },

  // rank 12 — King at A12 (file 0), then mixed
  { file: 1, rank: 12, shape: "square", value: 120 },
  { file: 2, rank: 12, shape: "triangle", value: 64 },
  { file: 3, rank: 12, shape: "triangle", value: 56 },
  { file: 4, rank: 12, shape: "triangle", value: 36 },
  { file: 5, rank: 12, shape: "triangle", value: 30 },
  { file: 6, rank: 12, shape: "square", value: 66 },
  { file: 7, rank: 12, shape: "square", value: 28 },

  // rank 13 — outer-flank squares
  { file: 0, rank: 13, shape: "square", value: 361 },
  { file: 1, rank: 13, shape: "square", value: 225 },
  { file: 6, rank: 13, shape: "square", value: 121 },
  { file: 7, rank: 13, shape: "square", value: 49 },
];

const WHITE_PYRAMID_PLACEMENT: PyramidPlacement = { file: 6, rank: 3, side: "white" };
const BLACK_PYRAMID_PLACEMENT: PyramidPlacement = { file: 0, rank: 12, side: "black" };

function pieceId(side: Side, shape: string, value: number, index: number): string {
  return `${side}-${shape}-${value}-${index}`;
}

function makePyramid(p: PyramidPlacement): Piece {
  const layerSpecs = p.side === "white" ? WHITE_PYRAMID_LAYERS : BLACK_PYRAMID_LAYERS;
  const totalValue = p.side === "white" ? WHITE_PYRAMID_VALUE : BLACK_PYRAMID_VALUE;
  const layers: PyramidLayer[] = layerSpecs.map((spec) => ({
    shape: spec.shape,
    value: spec.value,
    captured: false,
  }));
  return {
    id: pieceId(p.side, "pyramid", totalValue, 0),
    side: p.side,
    shape: "pyramid",
    value: totalValue,
    layers,
  };
}

function placeArmy(
  board: Map<SquareKey, Piece>,
  side: Side,
  placements: ReadonlyArray<Placement>,
): void {
  placements.forEach((placement, ix) => {
    const piece: Piece = {
      id: pieceId(side, placement.shape, placement.value, ix),
      side,
      shape: placement.shape,
      value: placement.value,
    };
    board.set(squareKey({ file: placement.file, rank: placement.rank }), piece);
  });
}

export function defaultVictoryConfig(): VictoryConfig {
  // V1 supports only Greater Victories; level defaults to "greater" (= Victoria Magna). The
  // commonThreshold / properMin* fields are kept on the type for now because V1.1 and V2 may
  // re-enable lesser victories — they're ignored under strict V1.
  return {
    level: "greater",
    commonThreshold: 0,
    properMinPieces: 0,
    properMinShapes: 0,
  };
}

export function createInitialState(victoryConfig: VictoryConfig = defaultVictoryConfig()): GameState {
  const board = new Map<SquareKey, Piece>();
  placeArmy(board, "white", WHITE_PLACEMENTS);
  placeArmy(board, "black", BLACK_PLACEMENTS);
  board.set(
    squareKey({ file: WHITE_PYRAMID_PLACEMENT.file, rank: WHITE_PYRAMID_PLACEMENT.rank }),
    makePyramid(WHITE_PYRAMID_PLACEMENT),
  );
  board.set(
    squareKey({ file: BLACK_PYRAMID_PLACEMENT.file, rank: BLACK_PYRAMID_PLACEMENT.rank }),
    makePyramid(BLACK_PYRAMID_PLACEMENT),
  );

  return {
    board,
    toMove: "black", // V1: Black (Odds) moves first.
    captured: { white: [], black: [] },
    turnNumber: 0,
    victoryConfig,
    victory: null,
    history: [],
  };
}

export function cloneState(state: GameState): GameState {
  return {
    board: new Map(
      Array.from(state.board, ([k, p]) => {
        if (p.shape === "pyramid" && p.layers) {
          return [k, { ...p, layers: p.layers.map((l) => ({ ...l })) }];
        }
        return [k, { ...p }];
      }),
    ),
    toMove: state.toMove,
    captured: {
      white: state.captured.white.map((p) => ({ ...p })),
      black: state.captured.black.map((p) => ({ ...p })),
    },
    turnNumber: state.turnNumber,
    victoryConfig: { ...state.victoryConfig },
    victory: state.victory ? { ...state.victory } : null,
    history: state.history.map((t) => ({ ...t, captures: [...t.captures] })),
  };
}
